import {
	type AppID,
	type DeepPartial,
	type NubeSDKSendableEvent,
	type NubeSDKState,
	type NubeSdkInternalEvent,
	type NubeSdkInternalEventData,
	isInternalEvent,
} from "@tiendanube/nube-sdk-internal-types";

export function createPostMessage(
	app: AppID,
	type: NubeSDKSendableEvent,
	changes?: DeepPartial<NubeSDKState>,
) {
	return JSON.stringify({ app, type, changes });
}

export function createErrorMessage(id: AppID, error: string) {
	return JSON.stringify({
		type: "WebWorkerError",
		app: id,
		error,
	});
}

export function dispatchMessage(
	app: AppID,
	type: NubeSDKSendableEvent,
	changes?: DeepPartial<NubeSDKState>,
) {
	return self.postMessage(createPostMessage(app, type, changes));
}

export function dispatchInternalMessage(
	app: AppID,
	type: NubeSdkInternalEvent,
	data: NubeSdkInternalEventData,
) {
	return self.postMessage(JSON.stringify({ app, type, data }));
}

export function dispatchError(id: AppID, error: string) {
	return self.postMessage(createErrorMessage(id, error));
}

export function listenMessages(
	id: AppID,
	handler: (
		event: NubeSDKSendableEvent,
		state?: Readonly<NubeSDKState>,
	) => void,
) {
	self.addEventListener("message", (message: MessageEvent<string>) => {
		try {
			const parsed = JSON.parse(message.data) as {
				type: string;
			};

			if (isInternalEvent(parsed.type)) {
				// Ignore internal messages
				return;
			}

			const sdkMessage = parsed as {
				type: NubeSDKSendableEvent;
				state?: Readonly<NubeSDKState>;
			};

			handler(sdkMessage.type, sdkMessage.state);
		} catch (err) {
			console.error(err);
			if (err instanceof Error) {
				dispatchError(id, err.message);
				return;
			}
			dispatchError(id, `Unable handle received message: ${err}`);
		}
	});
}

export function listenInternalMessages(
	id: AppID,
	handler: (
		event: NubeSdkInternalEvent,
		data: NubeSdkInternalEventData,
	) => void,
) {
	self.addEventListener("message", (message: MessageEvent<string>) => {
		try {
			const parsed = JSON.parse(message.data) as {
				type: string;
			};

			if (!isInternalEvent(parsed.type)) {
				// Ignore non-internal messages
				return;
			}

			const internalMessage = parsed as {
				type: NubeSdkInternalEvent;
				data: NubeSdkInternalEventData;
			};

			handler(internalMessage.type, internalMessage.data);
		} catch (err) {
			console.error(err);
			if (err instanceof Error) {
				dispatchError(id, err.message);
				return;
			}
			dispatchError(id, `Unable handle received message: ${err}`);
		}
	});
}

export function listenInternalMessageOnce<T extends NubeSdkInternalEventData>(
	id: AppID,
	event: NubeSdkInternalEvent,
	filter: (data: T) => boolean,
	handler: (data: T) => void,
) {
	let dispatched = false;

	const listener = (message: MessageEvent<string>) => {
		try {
			if (dispatched) return;

			const parsed = JSON.parse(message.data) as {
				type: string;
			};

			if (!isInternalEvent(parsed.type)) {
				// Ignore non-internal messages
				return;
			}

			if (parsed.type !== event) {
				return;
			}

			const internalMessage = parsed as { type: NubeSdkInternalEvent; data: T };

			if (filter(internalMessage.data)) {
				dispatched = true;
				self.removeEventListener("message", listener);
				handler(internalMessage.data);
			}
		} catch (err) {
			console.error(err);
			if (err instanceof Error) {
				dispatchError(id, err.message);
				return;
			}
			dispatchError(id, `Unable handle received message: ${err}`);
		}
	};

	self.addEventListener("message", listener);
}
